// Reusable Modal component
// Centralized modal logic to eliminate duplication across the app
// Handles overlay rendering, event listeners, accessibility, and unsaved changes detection
// CREATED: Base modal component with createPortal, Escape key, overlay click, and ARIA attributes
// FIXED: React internal error by properly memoizing handleCloseAttempt with useCallback and fixing useEffect dependencies
// FIXED: Moved early return after all hooks to comply with Rules of Hooks

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getPortalRoot } from '../../lib/portalUtils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  loading?: boolean;
  hasUnsavedChanges?: () => boolean;
  onUnsavedChangesAttempt?: () => void;
  ariaLabelledBy?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  loading = false,
  hasUnsavedChanges,
  onUnsavedChangesAttempt,
  ariaLabelledBy,
}: ModalProps) {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  const handleCloseAttempt = useCallback(() => {
    if (loading) return;

    // Check for unsaved changes
    if (hasUnsavedChanges && hasUnsavedChanges()) {
      if (onUnsavedChangesAttempt) {
        onUnsavedChangesAttempt();
      }
      return;
    }

    onClose();
  }, [loading, hasUnsavedChanges, onUnsavedChangesAttempt, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick && !loading) {
      handleCloseAttempt();
    }
  };

  // Handle Escape key
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeOnEscape, loading, handleCloseAttempt]);

  if (!isOpen) return null;

  const titleId = ariaLabelledBy || 'modal-title';

  return createPortal(
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className={`bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <h3 id={titleId} className="text-xl font-bold text-gray-900">
            {title}
          </h3>
          {showCloseButton && (
            <button
              onClick={handleCloseAttempt}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fermer"
              disabled={loading}
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        {children}
      </div>
    </div>,
    getPortalRoot()
  );
}
